/** Unified Accounts management — all customer accounts across the brand.
 *
 * Market-standard single view: type tabs (All / Wholesale / PIN / Owned by
 * resellers), owner visibility, search, and the working row actions
 * (top-up, CDRs deep link, IP auth for wholesale). Creation is type-aware
 * and supports ownership by a reseller (chain account ids, b1:r1:acme).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  AccountBalanceWallet as TopUpIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import AccountTable, { Account, CommonColumns } from "../components/accounts/AccountTable";
import { ColumnDef } from "@tanstack/react-table";
import apiClient from "../api/client";
import PageHeader from "../components/common/PageHeader";
import ImportAccountsDialog from "../components/accounts/ImportAccountsDialog";
import { Upload as UploadIcon } from "@mui/icons-material";
import { useNotify } from "../components/common/NotificationProvider";

type TypeFilter = "all" | "wholesale" | "pin_test" | "owned";

interface ResellerOption {
  id: string;
  account: string;
  level?: number;
}

interface CreateForm {
  type: "wholesale" | "pin_test";
  account: string;
  parent_id: string;
  company: string;
  email: string;
  credit_limit: string;
  balance: string;
}

const EMPTY_CREATE: CreateForm = {
  type: "wholesale",
  account: "",
  parent_id: "",
  company: "",
  email: "",
  credit_limit: "",
  balance: "",
};

/** Type-aware account creation dialog (brand-owned or reseller-owned). */
function CreateAccountDialog({
  open,
  resellers,
  defaultType,
  onClose,
  onSave,
}: {
  open: boolean;
  resellers: ResellerOption[];
  defaultType: TypeFilter;
  onClose: () => void;
  onSave: (data: CreateForm) => Promise<void>;
}) {
  const [form, setForm] = useState<CreateForm>({ ...EMPTY_CREATE, type: defaultType === "pin_test" ? "pin_test" : "wholesale" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY_CREATE,
        type: defaultType === "pin_test" ? "pin_test" : "wholesale",
      });
    }
  }, [open, defaultType]);

  const handleSubmit = async () => {
    if (!form.account.trim()) return;
    setBusy(true);
    try {
      await onSave(form);
    } finally {
      setBusy(false);
    }
  };

  const isWholesale = form.type === "wholesale";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Account</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Account Type</InputLabel>
            <Select
              value={form.type}
              label="Account Type"
              onChange={(e) => setForm({ ...form, type: e.target.value as "wholesale" | "pin_test" })}
            >
              <MenuItem value="wholesale">Wholesale client (IP-authenticated)</MenuItem>
              <MenuItem value="pin_test">PIN customer (prepaid)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Owner (Reseller)</InputLabel>
            <Select
              value={form.parent_id}
              label="Owner (Reseller)"
              onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
            >
              <MenuItem value="">— Brand (direct) —</MenuItem>
              {resellers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.account} (L{r.level ?? 1})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={isWholesale ? "Account Name" : "PIN"}
            required
            fullWidth
            value={form.account}
            onChange={(e) => setForm({ ...form, account: e.target.value })}
            placeholder={isWholesale ? "e.g. acme_wholesale" : "4-12 digits"}
            helperText={
              form.parent_id
                ? `Full id: b1:<owner>:<name>`
                : isWholesale
                ? "Full id: b1:<name>"
                : "Account id: b1:pin_<PIN>"
            }
          />
          {isWholesale && (
            <TextField
              label="Company Name"
              fullWidth
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder="e.g. Acme Telecom Corp"
            />
          )}
          {isWholesale && (
            <TextField
              label="Contact Email"
              fullWidth
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          )}
          <TextField
            label="Initial Balance"
            fullWidth
            type="number"
            value={form.balance}
            onChange={(e) => setForm({ ...form, balance: e.target.value })}
            InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
          />
          {isWholesale && (
            <TextField
              label="Credit Limit (postpaid)"
              fullWidth
              type="number"
              value={form.credit_limit}
              onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
              InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!form.account.trim() || busy}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Unified Accounts page. */
export default function AccountsPage() {
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(
    () => (searchParams.get("type") as TypeFilter) || "all"
  );
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [resellers, setResellers] = useState<ResellerOption[]>([]);
  const [routingProfiles, setRoutingProfiles] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [editForm, setEditForm] = useState({
    company: "",
    email: "",
    credit_limit: "",
    max_concurrent_channels: "",
    account_type: "*prepaid" as string,
    disabled: false,
    routing_profile_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [topUpAccount, setTopUpAccount] = useState<Account | null>(null);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpReason, setTopUpReason] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/api/v1/accounts/");
      setAccounts(response.data.accounts || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoutingProfiles = useCallback(async () => {
    try {
      const response = await apiClient.get("/api/v1/routing/profiles");
      setRoutingProfiles((response.data?.profiles || []).map((p: any) => ({ id: p.id, name: p.name })));
    } catch {
      // optional dropdown data
    }
  }, []);

  const fetchResellers = useCallback(async () => {
    try {
      const response = await apiClient.get("/api/v1/resellers/", { params: { page: 1, page_size: 500 } });
      setResellers(response.data?.resellers || []);
    } catch {
      // optional dropdown data
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
    fetchResellers();
    fetchRoutingProfiles();
  }, [fetchAccounts, fetchResellers, fetchRoutingProfiles]);

  /** Client-side type + search filter (list endpoint returns all accounts). */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (typeFilter === "wholesale" && a.customer_type !== "wholesale") return false;
      if (typeFilter === "pin_test" && a.customer_type !== "pin_test") return false;
      if (typeFilter === "owned" && !a.id.includes(":")) return false;
      if (q) {
        const hay = `${a.account} ${(a as any).company ?? ""} ${a.id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [accounts, typeFilter, search]);

  const counts = useMemo(
    () => ({
      all: accounts.length,
      wholesale: accounts.filter((a) => a.customer_type === "wholesale").length,
      pin_test: accounts.filter((a) => a.customer_type === "pin_test").length,
      owned: accounts.filter((a) => (a as any).parent_id).length,
    }),
    [accounts]
  );

  /** Apply balance adjustment. */
  const handleTopUp = async () => {
    if (!topUpAccount || !topUpAmount) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/v1/accounts/${encodeURIComponent(topUpAccount.id)}/balance`, {
        amount: parseFloat(topUpAmount),
        reason: topUpReason.trim() || undefined,
      });
      notify(`Balance updated for ${topUpAccount.account}`);
      setTopUpAccount(null);
      setTopUpAmount("");
      setTopUpReason("");
      await fetchAccounts();
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to update balance", "error");
    } finally {
      setBusy(false);
    }
  };

  /** Open the edit dialog prefilled from the selected account. */
  const handleEdit = (account: Account) => {
    setEditAccount(account);
    setEditForm({
      company: (account as any).company ?? "",
      email: (account as any).email ?? "",
      credit_limit: account.credit_limit != null ? String(account.credit_limit) : "",
      max_concurrent_channels:
        account.max_concurrent_channels == null ? "" : String(account.max_concurrent_channels),
      account_type: account.account_type || "*prepaid",
      disabled: account.disabled,
      routing_profile_id: (account as any).routing_profile_id ?? "",
    });
  };

  /** Save edits via PATCH /accounts/{id}. */
  const handleEditSave = async () => {
    if (!editAccount) return;
    setSaving(true);
    try {
      await apiClient.patch(`/api/v1/accounts/${encodeURIComponent(editAccount.id)}`, {
        company: editForm.company || null,
        email: editForm.email || null,
        credit_limit: editForm.credit_limit === "" ? null : parseFloat(editForm.credit_limit),
        max_concurrent_channels:
          editForm.max_concurrent_channels === ""
            ? null
            : Math.max(0, parseInt(editForm.max_concurrent_channels, 10) || 0),
        account_type: editForm.account_type,
        disabled: editForm.disabled,
        routing_profile_id: editForm.routing_profile_id || null,
      });
      notify(`Account ${editAccount.account} updated`);
      setEditAccount(null);
      await fetchAccounts();
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to update account", "error");
    } finally {
      setSaving(false);
    }
  };

  /** Type-aware account creation. */
  const handleCreate = async (form: CreateForm) => {
    try {
      const payload: Record<string, unknown> = {
        tenant: "b1",
        account:
          form.type === "wholesale"
            ? form.account.toLowerCase().replace(/\s+/g, "_")
            : form.account.trim(),
        account_type: form.type === "wholesale" ? "*postpaid" : "*prepaid",
        customer_type: form.type,
        balance: form.balance === "" ? 0 : parseFloat(form.balance),
        parent_id: form.parent_id || undefined,
      };
      if (form.type === "wholesale") {
        payload.credit_limit = form.credit_limit === "" ? null : parseFloat(form.credit_limit);
        payload.company = form.company || null;
        payload.email = form.email || null;
      } else {
        // PIN customers get a pin_<PIN> account id, billed by entered PIN
        payload.account = `pin_${form.account.trim()}`;
        payload.pin = form.account.trim();
      }
      const response = await apiClient.post("/api/v1/accounts", payload);
      notify(`Account ${response.data.id} created`);
      setCreateOpen(false);
      await fetchAccounts();
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to create account", "error");
      throw err;
    }
  };

  const columns = useMemo(() => {
    const cols: ColumnDef<Account, any>[] = [
      CommonColumns.accountName,
      {
        id: "owner",
        header: "Owner",
        accessorFn: (row) => (row as any).parent_id || "",
        cell: (info) => {
          const owner = info.getValue() as string;
          if (!owner) return <Chip label="Brand" size="small" variant="outlined" />;
          const r = resellers.find((x) => x.id === owner);
          return <Chip label={r ? r.account : owner} size="small" color="primary" variant="outlined" />;
        },
        enableSorting: false,
      },
      CommonColumns.balance,
      CommonColumns.creditLimit,
      CommonColumns.accountType,
      CommonColumns.status,
    ];
    return cols;
  }, [resellers]);

  const typeSpecificActions = useCallback(
    (account: Account) => (
      <>
        <Button
          size="small"
          startIcon={<TopUpIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setTopUpAccount(account);
          }}
        >
          Top Up
        </Button>
        {account.customer_type === "wholesale" && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/wholesale`);
            }}
          >
            IPs
          </Button>
        )}
      </>
    ),
    [navigate]
  );

  const handleViewCDRs = (account: Account) => {
    navigate(`/cdrs?account=${encodeURIComponent(account.id)}`);
  };

  return (
    <Box>
      <PageHeader
        title="Accounts"
        subtitle={`${filtered.length} of ${accounts.length} accounts`}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setImportOpen(true)}
            >
              Import
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Create Account
            </Button>
          </>
        }
      />

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={typeFilter}
          onChange={(_, v: TypeFilter) => setTypeFilter(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}
        >
          <Tab value="all" label={`All (${counts.all})`} />
          <Tab value="wholesale" label={`Wholesale (${counts.wholesale})`} />
          <Tab value="pin_test" label={`PIN (${counts.pin_test})`} />
          <Tab value="owned" label={`Reseller-owned (${counts.owned})`} />
        </Tabs>
        <Box sx={{ p: 2 }}>
          <TextField
            size="small"
            label="Search account / company / id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 320 }}
          />
        </Box>
      </Paper>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AccountTable
          accounts={filtered}
          columns={columns}
          onViewCDRs={handleViewCDRs}
          typeSpecificActions={typeSpecificActions}
          onEdit={handleEdit}
          onDelete={() =>
            notify(
              "Delete accounts from their dedicated page (type-specific safeguards apply)",
              "info"
            )
          }
        />
      )}

      {/* Edit account dialog */}
      <Dialog open={editAccount !== null} onClose={() => !saving && setEditAccount(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Account — {editAccount?.account}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Company Name"
              fullWidth
              value={editForm.company}
              onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
            />
            <TextField
              label="Contact Email"
              fullWidth
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Billing Type</InputLabel>
              <Select
                value={editForm.account_type}
                label="Billing Type"
                onChange={(e) => setEditForm({ ...editForm, account_type: e.target.value })}
              >
                <MenuItem value="*postpaid">Postpaid (credit limit)</MenuItem>
                <MenuItem value="*prepaid">Prepaid (balance)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Credit Limit"
              fullWidth
              type="number"
              value={editForm.credit_limit}
              onChange={(e) => setEditForm({ ...editForm, credit_limit: e.target.value })}
              InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
            />
            <TextField
              label="Max Concurrent Channels"
              fullWidth
              type="number"
              value={editForm.max_concurrent_channels}
              onChange={(e) => setEditForm({ ...editForm, max_concurrent_channels: e.target.value })}
              inputProps={{ min: 0 }}
              helperText="Blank = unlimited · 0 = block calls · enforced by the billing engine"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.disabled}
                  onChange={(e) => setEditForm({ ...editForm, disabled: e.target.checked })}
                />
              }
              label="Disabled (control-plane flag — billing engine enforcement pending)"
            />
            <TextField
              select
              label="Routing Profile"
              fullWidth
              value={editForm.routing_profile_id}
              onChange={(e) => setEditForm({ ...editForm, routing_profile_id: e.target.value })}
              helperText="Carrier pinning override — empty = brand default routing (manage profiles under Routing)"
            >
              <MenuItem value="">— Brand default —</MenuItem>
              {routingProfiles.map((pr) => (
                <MenuItem key={pr.id} value={pr.id}>{pr.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAccount(null)} disabled={saving}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={saving}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <ImportAccountsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/api/v1/accounts/import"
        post={async (ep, body) => (await apiClient.post(ep, body)).data}
        onImported={() => fetchAccounts()}
      />

      <CreateAccountDialog
        open={createOpen}
        resellers={resellers}
        defaultType={typeFilter}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
      />

      {/* Top-up dialog */}
      <Dialog open={topUpAccount !== null} onClose={() => setTopUpAccount(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Balance — {topUpAccount?.account}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Amount (negative to deduct)"
              type="number"
              fullWidth
              value={topUpAmount}
              onChange={(e) => setTopUpAmount(e.target.value)}
              InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
              autoFocus
            />
            <TextField
              label="Reason"
              fullWidth
              value={topUpReason}
              onChange={(e) => setTopUpReason(e.target.value)}
              placeholder="e.g. wire transfer ref #1234"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopUpAccount(null)}>Cancel</Button>
          <Button onClick={handleTopUp} variant="contained" disabled={!topUpAmount || busy}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
